import { Component, Input, OnInit } from '@angular/core';
import { XsI18nPipe } from '../../../../../../../../libs/pipes/src';
import { hd, IDeviceContract, LoggerService } from '../../../../../../../../libs/services';
import { Enums } from '../../../../../../../../libs/services/enums/enums';
import { LogicFault } from '../../../../shared/util/errors';
import { DlgConfirmActionService } from '../services/dlg-confirm-action.service';

@Component({
  selector: 'mc-actions-menu',
  templateUrl: './actions-menu.component.html',
  styleUrls: ['./actions-menu.component.scss'],
})
export class ActionsMenuComponent implements OnInit {
  @Input() device: IDeviceContract;

  private _dropDownItems: hd.IDropdownList;
  private _allMenuOptions: hd.IDropdownList;
  private logPrefix = 'deviceActionMenu: ';

  get dropDownItems() {
    return Object.values(this._dropDownItems);
  }

  constructor(
    private dialogConfirmActionService: DlgConfirmActionService,
    private xsI18n: XsI18nPipe,
    private $log: LoggerService
  ) {
    this._allMenuOptions = {
      Lock: {
        id: 'Lock',
        caption: this.xsI18n.transform('label_Lock'),
        glyphicon: 'ssp-icon-lock2',
      },
      CheckIn: {
        id: 'CheckIn',
        caption: this.xsI18n.transform('label_CheckIn'),
        glyphicon: 'ssp-icon-checkin',
      },
      SendMessage: {
        id: 'SendMessage',
        caption: this.xsI18n.transform('label_SendMessage'),
        glyphicon: 'ssp-icon-sendmessage',
      },
      ResetPasscode: {
        id: 'ResetPasscode',
        caption: this.xsI18n.transform('label_ResetPasscode'),
        glyphicon: 'ssp-icon-password',
      },
      RemovePasscode: {
        id: 'RemovePasscode',
        caption: this.xsI18n.transform('label_RemovePasscode'),
        glyphicon: 'ssp-icon-password',
      },
      UnEnroll: {
        id: 'UnEnroll',
        caption: this.xsI18n.transform('label_UnEnroll'),
        glyphicon: 'ssp-icon-unenroll',
      },
      Wipe: {
        id: 'Wipe',
        caption: this.xsI18n.transform('label_Wipe'),
        glyphicon: 'ssp-icon-wipe',
      },
    };
  }

  public getActionMenuOptions = (deviceContract: IDeviceContract): { [menuOption: string]: boolean } => {
    const actionMenus = {
      iOS: {
        Lock: true,
        CheckIn: true,
        SendMessage: true,
        ResetPasscode: false,
        RemovePasscode: true,
        UnEnroll: true,
        Wipe: true,
      },
      iMac: {
        Lock: true,
        CheckIn: true,
        SendMessage: false,
        ResetPasscode: false,
        RemovePasscode: false,
        UnEnroll: true,
        Wipe: true,
      },
      Android: {
        Lock: true,
        CheckIn: true,
        SendMessage: true,
        ResetPasscode: true,
        RemovePasscode: true,
        UnEnroll: true,
        Wipe: true,
      },
      AndroidPlus: {
        Lock: true,
        CheckIn: true,
        SendMessage: true,
        ResetPasscode: true,
        RemovePasscode: true,
        UnEnroll: true,
        Wipe: true,
      },
      WindowsPhone: {
        Lock: true,
        CheckIn: true,
        SendMessage: false,
        ResetPasscode: true,
        RemovePasscode: false,
        UnEnroll: false,
        Wipe: true,
      },
      WindowsModernDesktop: {
        Lock: false,
        CheckIn: true,
        SendMessage: false,
        ResetPasscode: false,
        RemovePasscode: false,
        UnEnroll: false,
        Wipe: true,
      },
      WindowsDesktop: {
        Lock: false,
        CheckIn: true,
        SendMessage: true,
        ResetPasscode: false,
        RemovePasscode: false,
        UnEnroll: false,
        Wipe: false,
      },
      WindowsCE: {
        Lock: false,
        CheckIn: true,
        SendMessage: true,
        ResetPasscode: true,
        RemovePasscode: false,
        UnEnroll: false,
        Wipe: false,
      },
      Linux: {
        Lock: false,
        CheckIn: true,
        SendMessage: false,
        ResetPasscode: false,
        RemovePasscode: false,
        UnEnroll: false,
        Wipe: false,
      },
      Tizen: {
        Lock: false,
        CheckIn: true,
        SendMessage: false,
        ResetPasscode: false,
        RemovePasscode: false,
        UnEnroll: false,
        Wipe: false,
      },
    };

    const family = deviceContract.family;
    const platform: Enums.DevicePlatform = Enums.DeviceFamily[deviceContract.platform];

    // special case when enrolling a Win 10 desktop to a Windows Modern device rule
    let familyName = Enums.DeviceFamily[family];
    if (platform && platform === Enums.DevicePlatform.WindowsDesktop10) {
      familyName = 'WindowsModernDesktop';
    }
    if (platform && platform === Enums.DevicePlatform.macOS) {
      familyName = 'iMac';
    }

    if (!family) {
      throw new LogicFault('Device Family does not exist: ' + family);
    } else if (!Object.keys(actionMenus).includes(family as any)) {
      throw new LogicFault('No actions are registered for platform: ' + familyName);
    }

    return actionMenus[family];
  };

  public performAction = ($event, listItem: hd.IDropdownListItem) => {
    $event.stopImmediatePropagation();
    const deviceAction = Enums.DeviceActions[listItem.id];
    this.dialogConfirmActionService.openDialog(this.device as any, deviceAction).subscribe(
      result => this.$log.debug(this.logPrefix + 'DialogConfirmAction.show() resolved with result:', result),
      error => this.$log.debug(this.logPrefix + 'DialogConfirmAction.show() reject with error:', error)
    );
  };

  public getDropDownItems = (): hd.IDropdownList => {
    if (!this._dropDownItems && this.device) {
      let displaySplitter = false;
      const menuOptions = this.getActionMenuOptions(this.device);
      this._dropDownItems = {};
      for (const optionName in menuOptions) {
        if (typeof menuOptions[optionName] === 'boolean' && !!menuOptions[optionName]) {
          this._dropDownItems[optionName] = this._allMenuOptions[optionName];
          if (
            !displaySplitter &&
            (this._dropDownItems[optionName].id === 'UnEnroll' || this._dropDownItems[optionName].id === 'Wipe')
          ) {
            this._dropDownItems[optionName]['customProperty'] = 'splitter';
            displaySplitter = true;
          }
        }
      }
    }

    return this._dropDownItems;
  };

  ngOnInit() {
    this.getDropDownItems();
  }
}
